import { ArrowRight, Play, Pause, Volume2, VolumeX, Maximize, Minimize, Volume1, PictureInPicture2 } from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { useFacebookPixel } from "@/hooks/useFacebookPixel";
import { useUserIp } from "@/hooks/useUserIp";
import { useIsMobile } from "@/hooks/use-mobile";
import { useClarityTracking } from "@/hooks/useClarityTracking";
import VideoFormOverlay from "./VideoFormOverlay";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const HeroSection = () => {
  const isMobile = useIsMobile();
  const [showMobileFormPopup, setShowMobileFormPopup] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPiP, setIsPiP] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [hasEnded, setHasEnded] = useState(false);
  const [showFormOverlay, setShowFormOverlay] = useState(false);
  const [showDelayedMobileCTA, setShowDelayedMobileCTA] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const volumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const formDelayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const trackedMilestones = useRef<Set<number>>(new Set());
  const videoUrl = "https://odxcxsdkwiklbkarzwaz.supabase.co/storage/v1/object/public/videos/Vsl%20Video%201.mp4";
  
  const { trackEvent } = useFacebookPixel();
  const userIp = useUserIp();
  const { trackVideoPlay, trackVideoMilestone, trackVideoPaused, trackVideoFullscreen } = useClarityTracking();
  
  const VIDEO_MILESTONES = [25, 50, 75, 95]; // 95% = completion

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const currentProgress = (video.currentTime / video.duration) * 100;
      setProgress(currentProgress);
      
      // Check and track milestones
      VIDEO_MILESTONES.forEach((milestone) => {
        if (currentProgress >= milestone && !trackedMilestones.current.has(milestone)) {
          trackedMilestones.current.add(milestone);
          
          // Track with Clarity
          trackVideoMilestone(milestone);
          
          trackEvent({
            eventName: 'VSLVideoComplete',
            eventSourceUrl: window.location.href,
            clientIpAddress: userIp || undefined,
            customData: {
              video_title: 'Double-Barrel Ad System VSL',
              video_duration: video.duration,
              content_category: 'VSL',
              video_milestone: milestone,
              milestone_label: milestone === 95 ? 'complete' : `${milestone}%`
            }
          });
        }
      });
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [trackEvent, userIp]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    
    // If this is the first real interaction, unmute and restart
    if (!hasUserInteracted) {
      video.currentTime = 0;
      video.muted = false;
      setIsMuted(false);
      setHasUserInteracted(true);
      trackVideoPlay();
      video.play();
      setIsPlaying(true);
      return;
    }
    
    // Simple toggle for all subsequent interactions - check video state directly
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
      trackVideoPaused();
    }
  }, [hasUserInteracted, trackVideoPlay, trackVideoPaused]);

  // Dedicated tap handler for video area
  const handleVideoTap = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    togglePlay();
  }, [togglePlay]);

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
      if (!newMuted && volume === 0) {
        setVolume(0.5);
        videoRef.current.volume = 0.5;
      }
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      if (newVolume === 0) {
        setIsMuted(true);
        videoRef.current.muted = true;
      } else if (isMuted) {
        setIsMuted(false);
        videoRef.current.muted = false;
      }
    }
  };

  const getVolumeIcon = () => {
    if (isMuted || volume === 0) return <VolumeX className="w-4 h-4 text-foreground" />;
    if (volume < 0.5) return <Volume1 className="w-4 h-4 text-foreground" />;
    return <Volume2 className="w-4 h-4 text-foreground" />;
  };

  // Calculate scrub position from clientX - memoized with useCallback
  const calculateScrubPosition = useCallback((clientX: number) => {
    if (!progressBarRef.current || !videoRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickPosition = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    videoRef.current.currentTime = clickPosition * videoRef.current.duration;
  }, []);

  // Mouse events for progress bar scrubbing
  const handleProgressMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
    calculateScrubPosition(e.clientX);
  }, [calculateScrubPosition]);

  // Touch events for progress bar scrubbing
  const handleProgressTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);
    calculateScrubPosition(e.touches[0].clientX);
  }, [calculateScrubPosition]);

  const handleProgressTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    calculateScrubPosition(e.touches[0].clientX);
  }, [calculateScrubPosition]);

  const handleProgressTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const toggleFullscreen = async () => {
    const container = videoContainerRef.current;
    const video = videoRef.current;
    
    if (!container || !video) return;
    
    trackVideoFullscreen();
    
    // Check if we're currently in fullscreen
    const fullscreenElement = document.fullscreenElement || 
      (document as any).webkitFullscreenElement;
    
    if (!fullscreenElement) {
      // Try to enter fullscreen
      if (container.requestFullscreen) {
        await container.requestFullscreen();
      } else if ((container as any).webkitRequestFullscreen) {
        (container as any).webkitRequestFullscreen();
      } else if ((video as any).webkitEnterFullscreen) {
        // iOS Safari - must use video element directly
        (video as any).webkitEnterFullscreen();
      }
      setIsFullscreen(true);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || 
        (document as any).webkitFullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    };
    
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Picture-in-Picture toggle
  const togglePiP = async () => {
    const video = videoRef.current;
    if (!video) return;
    
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (document.pictureInPictureEnabled) {
        await video.requestPictureInPicture();
      }
    } catch (error) {
      console.log('PiP not supported or failed:', error);
    }
  };

  // Listen for PiP state changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    const handlePiPEnter = () => setIsPiP(true);
    const handlePiPLeave = () => setIsPiP(false);
    
    video.addEventListener('enterpictureinpicture', handlePiPEnter);
    video.addEventListener('leavepictureinpicture', handlePiPLeave);
    
    return () => {
      video.removeEventListener('enterpictureinpicture', handlePiPEnter);
      video.removeEventListener('leavepictureinpicture', handlePiPLeave);
    };
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (volumeTimeoutRef.current) {
        clearTimeout(volumeTimeoutRef.current);
      }
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      if (formDelayTimeoutRef.current) {
        clearTimeout(formDelayTimeoutRef.current);
      }
    };
  }, []);

  // Global mouse event listeners for progress bar dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        calculateScrubPosition(e.clientX);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Autoplay video muted on page load (works on mobile too)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    // Ensure muted for autoplay policy
    video.muted = true;
    
    // Attempt to play with promise handling
    const playPromise = video.play();
    
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          // Autoplay started successfully
          setIsPlaying(true);
        })
        .catch(() => {
          // Autoplay prevented by browser
          console.log('Autoplay prevented by browser');
          setIsPlaying(false);
        });
    }
  }, []);

  // Show form overlay when paused (after watching some) or when ended
  useEffect(() => {
    const alreadySubmitted = localStorage.getItem('videoFormSubmitted') === 'true';
    if (alreadySubmitted) return;

    if (hasEnded) {
      setShowFormOverlay(true);
    } else if (!isPlaying && progress > 2) {
      // Show form after 2 seconds delay when paused (and watched at least 2%)
      formDelayTimeoutRef.current = setTimeout(() => {
        setShowFormOverlay(true);
      }, 2000);
    } else if (isPlaying) {
      // Clear timeout and hide form when playing
      if (formDelayTimeoutRef.current) {
        clearTimeout(formDelayTimeoutRef.current);
      }
      setShowFormOverlay(false);
      setHasEnded(false);
    }
  }, [isPlaying, hasEnded, progress]);

  const handleFormClose = () => {
    setShowFormOverlay(false);
  };

  const handleFormResume = () => {
    setShowFormOverlay(false);
    setHasEnded(false);
    if (videoRef.current) {
      // Resume from current position, only reset if video ended
      if (hasEnded) {
        videoRef.current.currentTime = 0;
      }
      videoRef.current.play();
      setIsPlaying(true);
    }
  };


  // Handle mouse activity for auto-hide controls
  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  const handleMouseLeave = () => {
    if (isPlaying) {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 1000);
    }
  };

  // Show controls when video is paused
  useEffect(() => {
    if (!isPlaying) {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    }
  }, [isPlaying]);

  // Delay mobile CTA by 1 second after showFormOverlay becomes true
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isMobile && showFormOverlay) {
      timer = setTimeout(() => {
        setShowDelayedMobileCTA(true);
      }, 1000);
    } else {
      setShowDelayedMobileCTA(false);
    }
    return () => clearTimeout(timer);
  }, [isMobile, showFormOverlay]);

  // Determine if controls should be visible
  const controlsVisible = !isPlaying || showControls;

  const handleVolumeMouseEnter = () => {
    if (volumeTimeoutRef.current) {
      clearTimeout(volumeTimeoutRef.current);
    }
    setShowVolumeSlider(true);
  };

  const handleVolumeMouseLeave = () => {
    volumeTimeoutRef.current = setTimeout(() => {
      setShowVolumeSlider(false);
    }, 300);
  };

  return (
    <section className="relative pt-32 pb-20 overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[350px] md:w-[700px] h-[350px] md:h-[500px] bg-orange-500/35 blur-[120px] rounded-full pointer-events-none"></div>
      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <div className="flex flex-col items-center text-center">
          <p className="text-[11px] font-medium tracking-[0.2em] text-muted-foreground uppercase mb-6">
            For Virtual Wholesalers
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight max-w-4xl mb-6 leading-[1.1]">
            <span className="text-gradient">
              Stop Chasing Leads.
            </span>
            <br />
            <span className="bg-gradient-to-br from-orange-400 to-orange-600 bg-clip-text text-transparent">
              Make Them Chase You.
            </span>
          </h1>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mb-10 leading-relaxed">
            We build automated acquisition systems using{" "}
            <strong className="text-foreground">Facebook & Google Ads</strong> to flood your CRM with
            motivated sellers every single day.
          </p>
          <div className="relative w-full max-w-3xl mb-10">
            {/* Background glow for video */}
            <div className="absolute -inset-4 bg-orange-500/20 blur-2xl rounded-3xl pointer-events-none"></div>
            <div 
              ref={videoContainerRef} 
              className={`relative w-full rounded-2xl overflow-hidden border border-stone-400/30 dark:border-stone-300/20 bg-card group shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] dark:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.7)] hover:shadow-[0_35px_60px_-15px_rgba(234,88,12,0.25)] hover:-translate-y-0.5 transition-all duration-300 ${isPlaying && !showControls ? 'cursor-none' : ''}`}
              style={{ touchAction: 'manipulation' }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              {/* Video Element */}
              {/* @ts-ignore - fetchpriority is valid HTML attribute */}
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                src={videoUrl}
                autoPlay
                muted
                playsInline
                preload="metadata"
                // @ts-ignore - webkit-playsinline for older iOS
                webkit-playsinline="true"
                poster="/images/video-poster.jpg"
                // @ts-ignore
                fetchpriority="high"
                onPlay={() => { setIsPlaying(true); setHasEnded(false); }}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setHasEnded(true)}
              />
              
              
              {/* Overlay when not playing or during muted autoplay */}
              {(!isPlaying || (isPlaying && !hasUserInteracted)) && (
                <div className="absolute inset-0 bg-background/40 transition-opacity duration-300" />
              )}
              
              {/* Full video tap area for play/pause */}
              <div 
                className="absolute inset-0 flex items-center justify-center cursor-pointer z-10"
                onClick={handleVideoTap}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  togglePlay();
                }}
              >
                {/* Show play button when paused or during muted autoplay */}
                {(!isPlaying || (isPlaying && !hasUserInteracted)) && (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300 pointer-events-none">
                    <Play className="w-8 h-8 text-white ml-1" fill="white" />
                  </div>
                )}
              </div>

              {/* Bottom controls bar - auto-hide when playing */}
              <div className={`absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-background/80 to-transparent transition-opacity duration-300 z-20 ${controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <div className="flex items-end justify-between pointer-events-auto">
                  {/* Text - only show when not playing */}
                  {!isPlaying && (
                    <div className="text-left">
                      <p className="text-orange-500 dark:text-orange-400 text-xs font-semibold tracking-wider uppercase mb-1">
                        Watch Now
                      </p>
                      <p className="text-foreground font-medium text-sm md:text-base">
                        How The "Double-Barrel" Ad System Works
                      </p>
                    </div>
                  )}
                  <div className={`flex items-center gap-2 ${isPlaying ? "ml-auto" : ""}`}>
                    {/* Time display */}
                    <span className="text-xs text-muted-foreground font-mono hidden sm:block">
                      {formatTime(videoRef.current?.currentTime || 0)} / {formatTime(duration)}
                    </span>
                    {/* Volume control */}
                    <div 
                      className="relative flex items-center"
                      onMouseEnter={handleVolumeMouseEnter}
                      onMouseLeave={handleVolumeMouseLeave}
                    >
                      {/* Vertical volume slider - hidden on mobile */}
                      <div 
                        className={`hidden md:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 pb-1 transition-all duration-200 ${showVolumeSlider ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
                      >
                        <div className="bg-card/95 backdrop-blur-sm rounded-lg px-3 py-4 shadow-lg border border-border">
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={isMuted ? 0 : volume}
                            onChange={handleVolumeChange}
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Volume control"
                            className="w-1 h-16 bg-muted rounded-full appearance-none cursor-pointer writing-vertical [writing-mode:vertical-lr] [direction:rtl] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-orange-500 [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-runnable-track]:bg-muted/50 [&::-webkit-slider-runnable-track]:rounded-full"
                          />
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMute();
                        }}
                        className="p-2 rounded-full bg-background/50 hover:bg-background/70 transition-colors"
                        aria-label={isMuted ? "Unmute video" : "Mute video"}
                      >
                        {getVolumeIcon()}
                      </button>
                    </div>
                    {/* Play/Pause button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePlay();
                      }}
                      className="p-2 rounded-full bg-background/50 hover:bg-background/70 transition-colors"
                      aria-label={isPlaying ? "Pause video" : "Play video"}
                    >
                      {isPlaying ? (
                        <Pause className="w-4 h-4 text-foreground" />
                      ) : (
                        <Play className="w-4 h-4 text-foreground" fill="currentColor" />
                      )}
                    </button>
                    {/* Picture-in-Picture button - hide on mobile as it's not well supported */}
                    {document.pictureInPictureEnabled && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePiP();
                        }}
                        className="p-2 rounded-full bg-background/50 hover:bg-background/70 transition-colors hidden md:block"
                        aria-label={isPiP ? "Exit picture-in-picture" : "Enter picture-in-picture"}
                      >
                        <PictureInPicture2 className={`w-4 h-4 ${isPiP ? 'text-orange-500' : 'text-foreground'}`} />
                      </button>
                    )}
                    {/* Fullscreen button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFullscreen();
                      }}
                      className="p-2 rounded-full bg-background/50 hover:bg-background/70 transition-colors"
                      aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                    >
                      {isFullscreen ? (
                        <Minimize className="w-4 h-4 text-foreground" />
                      ) : (
                        <Maximize className="w-4 h-4 text-foreground" />
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Progress bar with scrubbing support */}
                <div 
                  ref={progressBarRef}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleProgressMouseDown(e);
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    handleProgressTouchStart(e);
                  }}
                  onTouchMove={(e) => {
                    e.stopPropagation();
                    handleProgressTouchMove(e);
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    handleProgressTouchEnd();
                  }}
                  className="mt-3 h-3 md:h-2 bg-muted/50 rounded-full cursor-pointer group/progress touch-none select-none"
                >
                  <div className="relative h-full">
                    <div 
                      className="h-full bg-gradient-to-r from-orange-500 to-orange-600 rounded-full relative"
                      style={{ width: `${progress}%` }}
                    >
                      {/* Draggable thumb - always visible when dragging, otherwise on hover */}
                      <div 
                        className={`absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-md ${
                          isDragging ? 'opacity-100 scale-110' : 'opacity-0 group-hover/progress:opacity-100'
                        }`} 
                      />
                    </div>
                  </div>
                </div>
              </div>
              </div>
            
            {/* Desktop: Form Overlay on video */}
            {!isMobile && (
              <VideoFormOverlay
                isVisible={showFormOverlay}
                hasEnded={hasEnded}
                onClose={handleFormClose}
                onResume={handleFormResume}
              />
            )}

            {/* Mobile: CTA Button overlay ON the video with 1s delay */}
            {isMobile && showDelayedMobileCTA && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-background/90 backdrop-blur-sm animate-fade-in">
                <button
                  onClick={() => setShowMobileFormPopup(true)}
                  className="py-2.5 px-5 text-sm bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg hover:from-orange-600 hover:to-orange-700 transition-all"
                >
                  Get Your Free Strategy Call →
                </button>
                <button
                  onClick={handleFormResume}
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                >
                  Continue watching
                </button>
              </div>
            )}
            </div>

            {/* Mobile Form Popup */}
            <Dialog open={showMobileFormPopup} onOpenChange={setShowMobileFormPopup}>
              <DialogContent className="max-w-sm p-0 bg-background border-border">
                <VideoFormOverlay
                  isVisible={true}
                  hasEnded={hasEnded}
                  onClose={() => setShowMobileFormPopup(false)}
                  onResume={() => {
                    setShowMobileFormPopup(false);
                    handleFormResume();
                  }}
                  isInModal={true}
                />
              </DialogContent>
            </Dialog>
          </div>
          <div className="flex flex-col items-center gap-3">
            <a
              href="#schedule"
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-br from-orange-500 to-orange-600 text-white font-bold rounded-xl text-base transition-all animate-pulse-glow hover:-translate-y-0.5"
            >
              Book Your Strategy Call
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <p className="text-xs text-muted-foreground">Limited spots available for this month</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;